import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，你要找的文章不存在或已被删除。</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span>返回分类浏览</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const pageMeta = {
    title: `${article.title} - ${siteInfo.name}`,
    description: article.excerpt,
    keywords: article.tags.join(',') + ',动漫本子,同人收藏',
    canonicalUrl: `${siteInfo.url}/article/${article.slug}`,
    ogType: 'article',
    ogImage: article.imageUrl,
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${siteInfo.url}/article/${article.slug}#article`,
        headline: article.title,
        description: article.excerpt,
        image: article.imageUrl,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: { '@id': `${siteInfo.url}/#organization` },
        isPartOf: { '@id': `${siteInfo.url}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteInfo.url}/article/${article.slug}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${siteInfo.url}/category` },
          { '@type': 'ListItem', position: 3, name: article.categoryName, item: `${siteInfo.url}/category?tab=${article.categoryId.replace('cat-', '')}` },
          { '@type': 'ListItem', position: 4, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead meta={pageMeta} />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '分类浏览', href: '/category' },
            { label: article.categoryName, href: `/category?tab=${article.categoryId === 'cat-1' ? 'doujinshi' : article.categoryId === 'cat-2' ? 'artbooks' : article.categoryId === 'cat-3' ? 'comic-guides' : article.categoryId === 'cat-4' ? 'merchandise' : article.categoryId === 'cat-5' ? 'events' : 'collection-tips'}` },
            { label: article.title },
          ]}
        />

        <article className="py-8 md:py-10">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1.5 bg-accent-100 text-accent-800 rounded-full font-medium whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="text-sm text-foreground-500">{article.readTime}</span>
                <span className="text-sm text-foreground-400">{article.date}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-5 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-p:mb-4 prose-p:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">文章作者</p>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-lg p-4 transition-all duration-200 hover:bg-background-200/50 cursor-pointer"
                    >
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1">{ra.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}